import { useState, useEffect, useCallback } from 'react';
import { BackupService, BackupConfiguration, BackupJob, RestoreJob, BackupVerification } from '@/services/backup/backupService';

export const useBackup = () => {
  const [configurations, setConfigurations] = useState<BackupConfiguration[]>([]);
  const [backupJobs, setBackupJobs] = useState<BackupJob[]>([]);
  const [restoreJobs, setRestoreJobs] = useState<RestoreJob[]>([]);
  const [verifications, setVerifications] = useState<BackupVerification[]>([]);
  const [backupStats, setBackupStats] = useState<any>({
    total_configs: 0,
    active_configs: 0,
    total_backups: 0,
    successful_backups: 0,
    failed_backups: 0,
    storage_used: 0,
    last_backup_time: null
  });
  const [loading, setLoading] = useState(true);

  // Load all backup data
  const loadBackupData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading backup data...');
      
      const [configs, jobs, restores, verifs, stats] = await Promise.all([
        BackupService.getBackupConfigurations(),
        BackupService.getBackupJobs(),
        BackupService.getRestoreJobs(),
        BackupService.getBackupVerifications(),
        BackupService.getBackupStats()
      ]);

      setConfigurations(configs);
      setBackupJobs(jobs);
      setRestoreJobs(restores);
      setVerifications(verifs);
      setBackupStats(stats);
      console.log('Backup data loaded successfully:', { 
        configs: configs.length, 
        jobs: jobs.length, 
        stats: stats.total_backups 
      });
    } catch (error) {
      console.error('Error loading backup data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadBackupData();
  }, [loadBackupData]);

  // Auto-refresh backup jobs every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [jobs, stats] = await Promise.all([
          BackupService.getBackupJobs(),
          BackupService.getBackupStats()
        ]);
        setBackupJobs(jobs);
        setBackupStats(stats);
      } catch (error) {
        console.error('Error auto-refreshing backup data:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Create backup configuration
  const createBackupConfiguration = useCallback(async (config: {
    name: string;
    description?: string;
    backup_type: string;
    schedule_cron?: string;
    retention_days: number;
    storage_config: any;
  }): Promise<boolean> => {
    const configId = await BackupService.createBackupConfiguration(config);
    if (configId) {
      await loadBackupData(); // Refresh data
      return true;
    }
    return false;
  }, [loadBackupData]);

  // Toggle backup configuration status
  const toggleBackupConfiguration = useCallback(async (configId: string, isActive: boolean): Promise<boolean> => {
    const success = await BackupService.updateBackupConfigurationStatus(configId, isActive);
    if (success) {
      setConfigurations(prev => 
        prev.map(config => 
          config.id === configId 
            ? { ...config, is_active: isActive }
            : config
        )
      );
    }
    return success;
  }, []);

  // Create backup manually
  const createBackup = useCallback(async (configId: string): Promise<boolean> => {
    const success = await BackupService.createBackup(configId);
    if (success) {
      setTimeout(() => loadBackupData(), 2000); // Refresh after backup starts
    }
    return success;
  }, [loadBackupData]);

  // Schedule backup
  const scheduleBackup = useCallback(async (configId: string): Promise<boolean> => {
    const jobId = await BackupService.scheduleBackup(configId);
    if (jobId) {
      setTimeout(() => loadBackupData(), 1000); // Refresh after scheduling
      return true;
    }
    return false;
  }, [loadBackupData]);

  // Restore backup
  const restoreBackup = useCallback(async (
    backupJobId: string, 
    targetEnvironment: string = 'staging',
    selectedTables: string[] = []
  ): Promise<boolean> => {
    const success = await BackupService.restoreBackup(backupJobId, targetEnvironment, selectedTables);
    if (success) {
      setTimeout(() => loadBackupData(), 2000); // Refresh after restore starts
    }
    return success;
  }, [loadBackupData]);

  // Verify backup
  const verifyBackup = useCallback(async (backupJobId: string): Promise<boolean> => {
    const success = await BackupService.verifyBackup(backupJobId);
    if (success) {
      setTimeout(() => loadBackupData(), 1000); // Refresh after verification
    }
    return success;
  }, [loadBackupData]);

  // Get running jobs
  const runningJobs = backupJobs.filter(job => job.status === 'running');
  const runningRestores = restoreJobs.filter(job => job.status === 'running');

  return {
    // Data
    configurations,
    backupJobs,
    restoreJobs,
    verifications,
    backupStats,
    loading,
    runningJobs,
    runningRestores,
    
    // Actions
    createBackupConfiguration,
    toggleBackupConfiguration,
    createBackup,
    scheduleBackup,
    restoreBackup,
    verifyBackup,
    refreshData: loadBackupData
  };
};